import { NgZone } from '@angular/core';
import { Params } from '@angular/router';
import { AccessTokenFormat as AccessTokenFormatReact, AccessTokenSource as AccessTokenSourceReact } from '@office-start/auth-types';
import { DataBoundary as DataBoundaryReact, NetworkType as NetworkTypeReact, ServiceWorkerState as ServiceWorkerStateReact } from '@office-start/common-types';
import { initializeComponentContext } from '@office-start/component-context-service';
import { Document as OfficeStartDocument } from '@office-start/document-types';
import { DataSecurityLevel as DataSecurityLevelReact } from '@office-start/instrumentation-types';
import { ExtendedOfficeRouteItem as ExtendedOfficeStartRouteItem, MOSOfficeRouteItem, OfficeComponentContext, OfficeEnvironment, OfficeRoute as OfficeStartRoute, OfficeRouteItem as OfficeStartRouteItem, OfficeRouteItemType as OfficeStartRouteItemType, OfficeWorkload } from '@office-start/officehome-types';
import { initializeOTel, sendEvent } from '@office-start/otel-telemetry-service';
import { IDLE_HIGH_PRIORITY, IDLE_LOW_PRIORITY, IDLE_MEDIUM_PRIORITY, IDLE_VERYLOW_PRIORITY, IMMEDIATE_PRIORITY } from '@office-start/scheduler-service';
import { initializeTelemetryService } from '@office-start/telemetry-service';
import { AppName, AppPlatform, AppWorkload, AppWorkloadType, DataSecurityLevel as OtelDataSecurityLevel, DeviceType, DomainOrigin, EcsRing, Geo, PageName, RequestOrigin, TelemetryNamespace, UserSegment } from '@office-start/telemetry-types';
import { map } from 'rxjs/operators';

import { FluidHostContext } from '../../fluid-content/scopes/fluid-host-context';
import { FluidInstrumentationService } from '../../fluid-content/utils/fluid-instrumentation-service';
import { EncryptionConfigService } from '../../service-worker/encryption/encryption-config.service';
import { ServiceWorkerStateService } from '../../service-worker/service-worker-state.service';
import { UploadActionType, UploadLocalFileService } from '../../upload/upload-local-file/upload-local-file.service';
import { OfficeRoute } from '../constants/office-route.enum';
import { WorkloadQueryParameter } from '../constants/workload-query-parameter.enum';
import { AriaEventFactoryService } from '../instrumentation/aria.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { UserProfileService } from '../instrumentation/user-profile.service';
import { ErrorDialogErrorCategory, ErrorDialogErrorType, getErrorDialogErrorCategory } from '../models/error-dialog.model';
import { FileItem, FileType } from '../models/file-item.model';
import { ApplicationUrlParam } from '../models/jsapi.model';
import { DocumentInfo, DocumentQueryParams } from '../models/wac-host.model';
import { WrapperVersion } from '../models/wrapper-version.model';
import { AadAppId } from '../services/aad-app-id.injection-token';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { DataSecurityService } from '../services/data-security.service';
import { DeviceDetector, PlatformType } from '../services/device-detector.service';
import { ErrorDialogService } from '../services/error-dialog.service';
import { FloodgateService } from '../services/floodgate.service';
import { ExtendedOfficeRouteItem, OfficeRouteItem, OfficeRouteService } from '../services/office-route.service';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { PerformanceService } from '../services/performance.service';
import { SchedulePriority, SchedulerService } from '../services/scheduler.service';
import { WacContentService } from '../services/wac-content.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { ReactShellConfigService } from '../shared-context/react-shell-config.service';
import { UserContextService } from '../shared-context/user-context.service';

let bootstrapped = false;

// Common initialization logic required to run office-start service-backed code
// TODO: Consider exposing a single bootstrap function from office-start rather than separate bootstrappers for logging/icons/etc
export function bootstrapOfficeStart(
    aadAppId: AadAppId,
    accessTokenService: AccessTokenService,
    capabilitiesService: CapabilitiesService,
    contextService: ContextService,
    errorDialogService: ErrorDialogService,
    fluidHostContext: FluidHostContext,
    fluidInstrumentationService: FluidInstrumentationService,
    instrumentationService: InstrumentationService,
    officeHomeApiConfigService: OfficeHomeApiConfigService,
    officeRouteService: OfficeRouteService,
    startupConfigService: ClientStartupConfigService,
    performanceService: PerformanceService,
    ariaEventFactoryService: AriaEventFactoryService,
    serviceWorkerStateService: ServiceWorkerStateService,
    scheduler: SchedulerService,
    dataSecurityService: DataSecurityService,
    deviceDetector: DeviceDetector,
    ngZone: NgZone,
    wacContentService: WacContentService,
    workloadQueryParameterValue: WorkloadQueryParameter,
    uploadLocalFileService: UploadLocalFileService,
    userContextService: UserContextService,
    userProfileService: UserProfileService,
    reactShellConfigService: ReactShellConfigService,
    encryptionConfigService?: EncryptionConfigService,
    wrapperVersion?: WrapperVersion | null,
    floodgateService?: FloodgateService
): OfficeComponentContext {
    const context: OfficeComponentContext = {
        aadAppId,
        buildDateUtc: startupConfigService.config.sharedClientStartupContext.buildDateUtc,
        buildVersion: process.env.BUILD_ID,
        cdnDomain: startupConfigService.config.officeStartCdnDomain,
        correlationId: startupConfigService.config.sharedClientStartupContext.correlationId,
        dataBoundary: getDataBoundaryReact(startupConfigService.config.dataBoundary),
        defaultLinkTarget: contextService.sharedContext.defaultLinkTarget,
        deploymentEnvironment: startupConfigService.config.sharedClientStartupContext.deploymentEnvironment as OfficeEnvironment,
        domainOrigin: startupConfigService.config.sharedClientStartupContext.domainOrigin as DomainOrigin,
        ecsRing: startupConfigService.config.sharedClientStartupContext.ecsRing as EcsRing,
        flights: startupConfigService.config.sharedClientStartupContext.flights,
        geoName: startupConfigService.config.sharedClientStartupContext.geoName as Geo,
        getAccessToken: (consumer, forceFetch, resourceUrl, scope, _isBoot, claims, silent, capDialogData, claimsChallenge) => accessTokenService.getAccessToken(consumer as string as AccessTokenConsumer, forceFetch, resourceUrl, (silent as any) === false, { category: capDialogData ? getErrorDialogErrorCategory(capDialogData.category) : undefined, fileType: capDialogData ? FileItem.fileTypeNameToFileType(capDialogData.fileType) : undefined }, undefined, scope, claims, claimsChallenge).then(token =>
        ({
            success: token.success,
            token: token.token,
            format: token.format as string as AccessTokenFormatReact,
            source: token.source as string as AccessTokenSourceReact,
            expiryMs: token.expiryMs,
            error: token.error,
            clientId: token.clientId,
            scope: token.scope
        })),
        getCsrfToken: () => contextService.getCsrfToken(),
        getIdentity: () => Promise.resolve({
            authType: startupConfigService.config.authType,
            authVersion: startupConfigService.config.authVersion,
            encryptionConfig: encryptionConfigService?.config,
            isMsa: startupConfigService.config.authType === AuthType.MSA,
            isOrgId: startupConfigService.config.authType === AuthType.OrgId,
            isPipl: startupConfigService.config.isPipl,
            objectId: startupConfigService.config.objectId,
            puid: startupConfigService.config.puid,
            tenantId: startupConfigService.config.tenantId,
            userCountryCode: startupConfigService.config.userCountryCode,
            userPrincipalName: startupConfigService.config.userPrincipalName,
            identityIssuedTimeUnixMs: startupConfigService.config.identityIssuedTimeUnixMs,
            tenantRegionScope: startupConfigService.config.tenantRegionScope,
            dataBoundary: startupConfigService.config.dataBoundary,
            loginHint: startupConfigService.config.loginHint,
            activityTimeout: reactShellConfigService.reactShellConfig.activityTimeout,
            controlsClaim: reactShellConfigService.reactShellConfig.controlsClaim,
            signInState: reactShellConfigService.reactShellConfig.signinState,
            userDisplayName: reactShellConfigService.reactShellConfig.userDisplayName
        }),
        inMockMode: contextService.sharedContext.inMockMode,
        isCorpNet: startupConfigService.config.sharedClientStartupContext.corpNet,
        jwtAuthEnabled: officeHomeApiConfigService.config.jwtAuthEnabled,
        officeHomeApiRoot: contextService.sharedContext.officeHomeApiRoot,
        officeMarketLcid: contextService.sharedContext.officeMarketLcid,
        officeStartManifestVersion: contextService.sharedContext.officeStartManifestVersion,
        preferredCulture: contextService.sharedContext.officeMarket,
        requestOrigin: startupConfigService.config.sharedClientStartupContext.requestOrigin as RequestOrigin,
        router: {
            getCurrentRoute: () => officeStartRouteFromOfficeHomeRoute(officeRouteService.getCurrentRoute()),
            getCurrentRouteQueryParams: () => officeRouteService.getCurrentRouteQueryParams(),
            getCurrentPageForLogging: () => officeRouteService.getCurrentPageForLogging(),
            getRouteObservable: () => officeRouteService.getRouteChangeEndObservable().pipe(map(event => officeStartRouteFromOfficeHomeRoute(event.currentRoute))),
            getSearchParams: () => {
                const params = officeRouteService.getCurrentRouteQueryParams();
                return new URLSearchParams(params);
            },
            getUrlForRoute: (route, additionalParams) => officeRouteService.getUrlForRoute(officeHomeRouteFromOfficeStartRoute(route), additionalParams),
            navigateTo: (route, replace, params, storageKey, eagerLoad) => (
                // When this gets called from React, we need to call router APIs in Angular Zone so that change detection gets picked up by all
                // Angular components. Otherwise, it can sometimes take seconds before navigation happens. We have to use ngZone.runTask() instead of
                // ngZone.run() because the latter queues a microtask, which might end up being executed too early and Angular would miss a change
                // detection cycle until a new task is queued (certain events on Office.com trigger this such as clicking)
                ngZone.runTask(() =>
                    officeRouteService.navigateTo(officeHomeRouteFromOfficeStartRoute(route), replace, params, storageKey, eagerLoad))
            ),
            navigateBack: () => (
                ngZone.runTask(() => officeRouteService.back())
            ),
            openHostedDocument: (document, startTime, wdOrigin, fluid, fluidHostScenarioName, additionalParams) => {
                // TODO: This is probably temporary until we figure out how to bootstrap in office-start
                // This currently assumes only fluid or wac files
                if (fluid) {
                    fluidInstrumentationService.logPerformanceBootStart(
                        fluidHostContext.getBootId()
                    );
                    fluidHostContext.hostScenarioName = fluidHostScenarioName;
                    fluidHostContext.hostScenarioStartTime = startTime;
                    return officeRouteService.navigateTo(
                        { route: OfficeRoute.Content, app: FileType.Fluid },
                        false,
                        WacContentService.getWacParams(
                            document.driveId,
                            document.docId,
                            document.canonicalUrl,
                            document.sharepointIds ? { siteUrl: document.sharepointIds.siteUrl } : undefined
                        )
                    );
                } else {
                    const nav = additionalParams?.[ApplicationUrlParam.Nav];
                    const wopiFetchData = wacContentService.bootstrapAndRoute(
                        getDocumentInfoFromOfficeStartDocument(document, wdOrigin, additionalParams),
                        nav ? { [DocumentQueryParams.Nav]: nav } : undefined,
                        startTime
                    );
                    return Promise.resolve(!!wopiFetchData);
                }
            }
        },
        sessionId: startupConfigService.config.sharedClientStartupContext.sessionId,
        stringsContainerName: 'officestartstrings', // TODO: Move to office-start settings
        workload: workloadQueryParameterValue as string as OfficeWorkload,
        wrapperVersion: wrapperVersion ?? undefined,
        oneDriveUrl: contextService.sharedContext.oneDriveUrl,
        oneDriveResourceId: contextService.sharedContext.oneDriveResourceId,
        sharePointResourceId: contextService.sharedContext.sharePointResourceId,
        loginHint: startupConfigService.config.loginHint,
        uiContext: {
            openUploadLocalModal: (path, name, action) => uploadLocalFileService.showModal(path, name, action as unknown as UploadActionType),
            showErrorDialog: errorDialog => errorDialogService.showErrorDialog({
                ...errorDialog,
                category: errorDialog.category as ErrorDialogErrorCategory,
                error: errorDialog.error as ErrorDialogErrorType,
                fileType: FileType[errorDialog.fileType]
            })
        },
        recordFloodgate: (events: Array<string>) => floodgateService?.recordFloodgate(events),
        testTraffic: reactShellConfigService.reactShellConfig.testTraffic,
        thirdPartyNoticeUrl: reactShellConfigService.reactShellConfig?.thirdPartyNoticeUrl,
        logoutUrl: reactShellConfigService.reactShellConfig?.logoutUrl,
        appSwitchUrl: reactShellConfigService.reactShellConfig?.acctSwitchingConfig?.appSwitchUrl,
        appSwitchToUrl: reactShellConfigService.reactShellConfig?.acctSwitchingConfig?.appSwitchToUrl
    } as OfficeComponentContext; // TODO: Remove type assertion once interface changes have synced across repos

    if (!bootstrapped) {
        bootstrapped = true;
        import(
            /*webpackChunkName: "instrumentation-service" */ '@office-start/instrumentation-service'
        ).then(mod => {
            mod.initializeInstrumentation(
                [
                    {
                        // Aria
                        logEvent: (streamName, params, metrics) => ariaEventFactoryService.createEvent(streamName, params as InstrumentationParams, metrics).record(),
                        flush: () => {
                            ariaEventFactoryService.flush();
                        }
                    }
                ],
                params => {
                    mod.applyCommonStateParams(
                        params,
                        officeRouteService.getCurrentPageForLogging() ? officeRouteService.getCurrentPageForLogging() : OfficeRoute.OfficeHome,
                        officeRouteService.getInitialPageForLogging() ? officeRouteService.getInitialPageForLogging() :  OfficeRoute.OfficeHome,
                        ServiceWorkerStateReact[serviceWorkerStateService.currentState],
                        DataSecurityLevelReact[dataSecurityService.securityLevel.toString()],
                        NetworkTypeReact[deviceDetector.getNetworkInfo()]
                    );
                }
            );
        });

        initializeComponentContext(context);

        const otelInitializationPriority = startupConfigService.config.sharedClientStartupContext.clientOTelImmediateSchedulePriorityEnabled ? SchedulePriority.Immediate : startupConfigService.config.sharedClientStartupContext.clientOTelHighSchedulePriorityEnabled ? SchedulePriority.High :  startupConfigService.config.sharedClientStartupContext.clientOTelMediumSchedulePriorityEnabled ? SchedulePriority.Medium : SchedulePriority.Low;
        scheduler.scheduleWork(() => {
            initializeOTel(
                {
                    appName: startupConfigService.config.sharedClientStartupContext.oTelAppName as AppName,
                    appPlatform: getAppPlatform(workloadQueryParameterValue),
                    appWorkload: getAppWorkload(workloadQueryParameterValue),
                    appWorkloadType: wrapperVersion ? getAppWorkloadType(wrapperVersion, capabilitiesService) : undefined,
                    bundleBuildDate: startupConfigService.config.sharedClientStartupContext.buildDateUtc,
                    bundleBuildId: startupConfigService.config.sharedClientStartupContext.buildId,
                    correlationId: startupConfigService.config.sharedClientStartupContext.correlationId,
                    dataBoundary: getDataBoundaryReact(startupConfigService.config.dataBoundary),
                    deploymentEnvironment: context.deploymentEnvironment,
                    deviceType: getDeviceType(deviceDetector),
                    domainOrigin: startupConfigService.config.sharedClientStartupContext.domainOrigin as DomainOrigin,
                    ecsRing: startupConfigService.config.sharedClientStartupContext.ecsRing as EcsRing,
                    firstVisitAgeInDays: userProfileService?.fullDaysSinceFirstVisit(),
                    flights: startupConfigService.config.sharedClientStartupContext.flights,
                    identity: {
                        isMsa: startupConfigService.config.authType === AuthType.MSA,
                        objectId: startupConfigService.config.objectId,
                        puid: startupConfigService.config.puid,
                        tenantId: startupConfigService.config.tenantId,
                        encryptionConfig: encryptionConfigService?.config,
                        userPrincipalName: startupConfigService.config.userPrincipalName,
                        authType: startupConfigService.config.authType,
                        isOrgId: startupConfigService.config.authType === AuthType.OrgId
                    },
                    initialPageName: (officeRouteService.getInitialPageForLogging() ? officeRouteService.getInitialPageForLogging() :  OfficeRoute.OfficeHome) as PageName,
                    isCorpNet: startupConfigService.config.sharedClientStartupContext.corpNet,
                    IsEudbEndpointEnabled: startupConfigService.config.sharedClientStartupContext.oTelEudbEndpointEnabled,
                    IsSamplingEnabled: startupConfigService.config.sharedClientStartupContext.oTelSamplingEnabled,
                    isTestTraffic: contextService.sharedContext.inMockMode,
                    networkType: NetworkTypeReact[deviceDetector.getNetworkInfo()],
                    oTelAriaNameSpace: startupConfigService.config.sharedClientStartupContext.oTelNameSpace as TelemetryNamespace,
                    oTelAriaTenant: startupConfigService.config.sharedClientStartupContext.oTelAriaTenant,
                    oTelEnabled: startupConfigService.config.sharedClientStartupContext.oTelEnabled,
                    preferredCulture: contextService.sharedContext.officeMarket,
                    requestOrigin: context.requestOrigin,
                    schedulerPriority: getOtelInitializationPriority(otelInitializationPriority),
                    serverLocation: startupConfigService.config.sharedClientStartupContext.geoName.toLowerCase() as Geo,
                    serviceWorkerState: ServiceWorkerStateReact[serviceWorkerStateService.currentState],
                    sessionId: context.sessionId,
                    userSegment: getUserSegment(userContextService, userProfileService),
                    win11DetectionEnabled: startupConfigService.config.sharedClientStartupContext.oTelWin11DetectionEnabled,
                    oTelDiagnosticAriaNameSpace: startupConfigService.config.sharedClientStartupContext.oTelDiagnosticEnabled ? startupConfigService.config.sharedClientStartupContext.oTelDiagnosticNameSpace : undefined,
                    oTelDiagnosticAriaTenant: startupConfigService.config.sharedClientStartupContext.oTelDiagnosticEnabled ? startupConfigService.config.sharedClientStartupContext.oTelDiagnosticAriaTenant : undefined
                }
            )
                .then(() => {
                    initializeTelemetryService(
                        [
                            {
                                // Otel
                                logEvent: sendEvent,
                                // eslint-disable-next-line @typescript-eslint/no-empty-function
                                flush: () => undefined
                            }
                        ],
                        () => officeStartRouteFromOfficeHomeRoute(officeRouteService.getCurrentRoute()),
                        {
                            actionEnabled: startupConfigService.config.sharedClientStartupContext.oTelClickEnabled,
                            diagnosticEnabled: startupConfigService.config.sharedClientStartupContext.oTelDiagnosticEnabled,
                            errorEnabled: startupConfigService.config.sharedClientStartupContext.oTelErrorEnabled,
                            featureEnabled: startupConfigService.config.sharedClientStartupContext.oTelFeatureEnabled,
                            impressionEnabled: startupConfigService.config.sharedClientStartupContext.oTelImpressionEnabled,
                            pageViewEnabled: startupConfigService.config.sharedClientStartupContext.oTelPageViewEnabled,
                            perfEnabled: startupConfigService.config.sharedClientStartupContext.oTelPerfEnabled,
                            requestEnabled: startupConfigService.config.sharedClientStartupContext.oTelRequestEnabled
                        },
                        {
                            perfSamplingEnabled: startupConfigService.config.sharedClientStartupContext.oTelSamplingPerformanceEnabled,
                            requestSamplingEnabled: startupConfigService.config.sharedClientStartupContext.oTelSamplingRequestEnabled
                        },
                        getDataSecurityLevel(dataSecurityService.securityLevel),
                        startupConfigService.config.sharedClientStartupContext.flights // Set up Flights info for Session event
                    );
                }).catch(error => {
                    // TODO: Should change to use OTEL error logging once API is available
                    const params: InstrumentationParams = {
                        [InstrumentationParam.Error]: error?.toString?.() || ''
                    };
                    instrumentationService.logDebugCacheEvent(KnownInstrumentationEvents.OtelInitializationError, params);
                });
            performanceService.mark(KnownInstrumentationEvents.OtelReady);
        }, otelInitializationPriority);
    }

    return context;
}

function officeHomeRouteFromOfficeStartRoute(officeStartRoute: OfficeStartRouteItem): OfficeRouteItem {
    if (!officeStartRoute.route) {
        return undefined;
    }

    const route = officeStartRoute.route as OfficeRoute;
    switch (route) {
        case OfficeRoute.M365Apps:
            const mosOfficeRouteItem = officeStartRoute as MOSOfficeRouteItem;
            if (mosOfficeRouteItem) {
                return {
                    route,
                    appId: mosOfficeRouteItem.appId,
                    entityId: mosOfficeRouteItem.entityId,
                    subEntityId: mosOfficeRouteItem.subPageId
                };
            }
            break;
        case OfficeRoute.MyContent:
            const myContentRoute = officeStartRoute as ExtendedOfficeStartRouteItem<OfficeStartRouteItemType.Base, 'MyContent'>;
            return {
                route: OfficeRoute.MyContent,
                entityId: myContentRoute.entityId,
                subEntityId: myContentRoute.subEntityId
            };
        default:
            return {
                route,
                app: FileType[officeStartRoute.app]
            };
    }
}

function officeStartRouteFromOfficeHomeRoute(officeHomeRoute?: OfficeRouteItem): OfficeStartRouteItem {
    if (!officeHomeRoute?.route) {
        return undefined;
    }
    const route = officeHomeRoute.route as OfficeStartRoute;
    switch (officeHomeRoute.route) {
        case OfficeRoute.M365Apps:
            const mosOfficeRouteItem : MOSOfficeRouteItem = {
                route: 'M365Apps',
                app: officeHomeRoute.appId,
                entityId: officeHomeRoute.entityId,
                subPageId: officeHomeRoute.subEntityId
            };
            return mosOfficeRouteItem;
        case OfficeRoute.MyContent:
            const myContentRoute = officeHomeRoute as ExtendedOfficeRouteItem<OfficeRoute.MyContent>;
            return {
                route: OfficeRoute.MyContent,
                entityId: myContentRoute.entityId,
                subEntityId: myContentRoute.subEntityId
            };
        default:
            return {
                route,
                app: FileType[officeHomeRoute.app]
            };
    }
}

function getDocumentInfoFromOfficeStartDocument(document: OfficeStartDocument, wdOrigin: string, additionalParams: Params): DocumentInfo {
    return {
        docId: document.docId,
        docUrl: document.canonicalUrl,
        driveId: document.driveId,
        fileType: FileType[document.fileType],
        fileName: document.fileName,
        wdOrigin,
        applicationUrlParams: additionalParams
    };
}

function getUserSegment(userContextService: UserContextService, userProfileService: UserProfileService): UserSegment {
    return {
        isAdmin: userContextService?.user?.isAdmin,
        isDynamics: userContextService?.user?.isDynamics,
        isEdu: userContextService?.user?.isEdu,
        isEduFaculty: userContextService?.user?.isEduFaculty,
        isEduStudent: userContextService?.user?.isEduStudent,
        isFirstVisit: userProfileService?.isFirstVisit(),
        isIw: userContextService?.user?.isIw,
        isSmb: userContextService?.user?.isSmb
    };
}

function getAppWorkload(workloadQueryParameterValue: WorkloadQueryParameter): AppWorkload {
    switch (workloadQueryParameterValue) {
        case WorkloadQueryParameter.OfficeHome:
        case WorkloadQueryParameter.OfficeHomeReact:
            return 'Web';
        case WorkloadQueryParameter.Pwa:
            return 'HWA';
        case WorkloadQueryParameter.Embed:
            return 'Embed';
        default:
            return 'Web';
    }
}

function getAppPlatform(workloadQueryParameterValue: WorkloadQueryParameter): AppPlatform {
    switch (workloadQueryParameterValue) {
        case WorkloadQueryParameter.Pwa:
            return 'Universal';
        default:
            return 'Web';
    }
}

function getDeviceType(deviceDetector: DeviceDetector): DeviceType {
    switch (deviceDetector.getDeviceInfo().platform.platformType) {
        case PlatformType.AndroidPhone:
            return 'AndroidPhone';
        case PlatformType.AndroidTablet:
            return 'AndroidTablet';
        case PlatformType.Chromebook:
            return 'Chromebook';
        case PlatformType.Linux:
            return 'Linux';
        case PlatformType.Mac:
            return 'Mac';
        case PlatformType.PC:
            return 'PC';
        case PlatformType.WindowsPhone:
            return 'WindowsPhone';
        case PlatformType.iPad:
            return 'iPad';
        case PlatformType.iPhone:
            return 'iPhone';
        default:
            return 'Other';
    }
}

function getDataSecurityLevel(dataSecurityLevel: DataSecurityLevel): OtelDataSecurityLevel {
    switch (dataSecurityLevel) {
        case DataSecurityLevel.Basic:
            return OtelDataSecurityLevel.Basic;
        case DataSecurityLevel.Enhanced:
            return OtelDataSecurityLevel.Enhanced;
        case DataSecurityLevel.Security:
            return OtelDataSecurityLevel.Security;
        case DataSecurityLevel.Full:
            return OtelDataSecurityLevel.Full;
    }
}

function getAppWorkloadType(wrapperVersion: WrapperVersion, capabilitiesService: CapabilitiesService): AppWorkloadType {
    if (capabilitiesService.capabilities.isPwaInWebView2) {
        return 'WebView2';
    } else if (capabilitiesService.capabilities.isPwaInUwp) {
        const webView2Version = new RegExp('1[0-9]{2}10');
        if (wrapperVersion?.build && webView2Version.test((wrapperVersion.build).toString())) {
        // UWP appType launched as a fallback due to WebView2 runtime not installed on end user system
        return 'UWP_fallback';
    } else {
        return 'UWP';
        }
    }
}

function getOtelInitializationPriority(otelInitializationPriority: SchedulePriority ) {
    switch (otelInitializationPriority) {
        case SchedulePriority.VeryLow:
            return IDLE_VERYLOW_PRIORITY;
        case SchedulePriority.Low:
            return IDLE_LOW_PRIORITY;
        case SchedulePriority.Medium:
            return IDLE_MEDIUM_PRIORITY;
        case SchedulePriority.High:
            return IDLE_HIGH_PRIORITY;
        case SchedulePriority.Immediate:
            return IMMEDIATE_PRIORITY;
    }
}

function getDataBoundaryReact(dataBoundary: string): DataBoundaryReact {
    switch (dataBoundary) {
        case 'EU':
            return 'EU';
        default:
            return undefined;
    }
}
